import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Instagram, Facebook } from "lucide-react";
import InquiryForm from "../components/InquiryForm";

const CHAPTERS = [
  {
    no: "01",
    title: "Where it all started",
    body:
      "Kanwal pretty much grew up in the kitchen, watching her mom cook. From a young age she was already helping cater for her mom's friends and pulling together the food for family parties — feeding a room full of people just came second nature to her. Harris, honestly, always had a thing for cooking too. Any chance he got he'd be right there in the kitchen with his mom, tasting and learning as he went.",
    img: "https://images.unsplash.com/photo-1601723897386-e5df0c749fb7?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    alt: "Cumin seeds and spices",
  },
  {
    no: "02",
    title: "The recipes our elders trusted us with",
    body:
      "Nothing we cook comes from a book. Every masala ratio, every \"cook it till it looks right,\" was pressed into our hands by the people who raised us. We'd stand at their elbows, notebooks forgotten, just watching and tasting. Those lessons — patience, generosity, a heavy hand with the ghee — are the real ingredients.",
    img: "https://images.unsplash.com/photo-1728910156510-77488f19b152?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    alt: "A desi feast spread on the table",
  },
  {
    no: "03",
    title: "Falling in love, one meal at a time",
    body:
      "When we found each other, we discovered we already spoke the same language. Our first dates were spent happily arguing over whose family made the better biryani. We cooked our way through every celebration and every ordinary Tuesday — and somewhere between the karahi and the chai, we built a home.",
    img: "/harris-kanwal.png",
    alt: "Harris and Kanwal at their Nosh Catering booth",
    pos: "object-[50%_18%]",
  },
  {
    no: "04",
    title: "Sharing our table with yours",
    body:
      "Nosh is that very same food, made the very same way — for you. When you order from us you aren't just getting catering; you're pulling up a chair at our family table. That's our whole reason for doing this: to share the flavours we grew up on with the rest of the world.",
    img: "https://images.unsplash.com/photo-1631515243349-e0cb75fb8d3a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    alt: "A bowl of biryani ready to serve",
  },
];

const contactCards = [
  { icon: Phone, label: "Call or text", value: "201-620-4376", href: "tel:2016204376" },
  { icon: MapPin, label: "Where we cater", value: "New Jersey & New York" },
  { icon: Clock, label: "Best to book", value: "At least 3–5 days ahead" },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-nosh-cream">
      {/* Header */}
      <header className="border-b border-nosh-border bg-nosh-surface pb-16 pt-40">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <div className="mb-1 font-script text-3xl text-nosh-gold">Harris &amp; Kanwal</div>
            <h1 className="font-display text-5xl font-bold leading-[1.02] text-nosh-ink sm:text-6xl lg:text-7xl">
              Our story starts in the kitchen
            </h1>
            <p className="mt-6 max-w-xl font-body text-lg text-nosh-ink/65">
              A husband and wife who fell in love with each other — and with feeding people. This is
              how a lifetime of home cooking became Nosh.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Manifesto chapters */}
      <section data-testid="manifesto" className="py-24 md:py-32">
        <div className="mx-auto max-w-[1200px] space-y-24 px-5 md:px-10 md:space-y-32">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.no}
              initial={{ opacity: 0, y: 46 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
            >
              <div className="[direction:ltr]">
                <div className="relative aspect-[4/3] overflow-hidden rounded-[2rem] border border-nosh-border shadow-xl">
                  <img src={c.img} alt={c.alt} className={`h-full w-full object-cover ${c.pos || ""}`} />
                </div>
              </div>
              <div className="[direction:ltr]">
                <div className="font-display text-6xl font-bold text-nosh-gold/40 md:text-7xl">{c.no}</div>
                <h2 className="mt-2 font-display text-3xl font-bold leading-tight text-nosh-ink sm:text-4xl">{c.title}</h2>
                <p className="mt-5 font-body text-lg leading-relaxed text-nosh-ink/70 first-letter:float-left first-letter:mr-2 first-letter:font-display first-letter:text-6xl first-letter:font-bold first-letter:leading-[0.8] first-letter:text-nosh-green">
                  {c.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Quote band */}
      <section className="bg-nosh-green py-20 text-center md:py-28">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <p className="font-display text-3xl font-medium italic leading-tight text-nosh-cream sm:text-4xl lg:text-5xl">
            "We don't cook to impress. We cook the way we were taught — with love, patience, and
            enough for everyone to have seconds."
          </p>
          <p className="mt-6 font-script text-3xl text-nosh-gold">— Harris &amp; Kanwal</p>
        </div>
      </section>

      {/* Contact + inquiry */}
      <section id="inquiry" data-testid="contact-section" className="scroll-mt-24 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="mb-14 max-w-2xl">
            <div className="mb-1 font-script text-3xl text-nosh-gold">Get in touch</div>
            <h2 className="font-display text-4xl font-bold leading-none text-nosh-ink sm:text-5xl lg:text-6xl">Let's plan your feast</h2>
            <p className="mt-5 font-body text-lg text-nosh-ink/65">
              Tell us about your event, pick a date, and choose pickup or delivery. We'll get back to
              you with a friendly quote — usually within a day.
            </p>
          </div>

          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            {/* Contact info */}
            <div className="space-y-4">
              {contactCards.map((c) => {
                const Icon = c.icon;
                const inner = (
                  <div className="flex items-start gap-4 rounded-2xl border border-nosh-border bg-white p-5 transition-colors hover:border-nosh-green">
                    <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-nosh-green text-nosh-cream">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-poster text-[11px] font-bold uppercase tracking-widest text-nosh-gold">{c.label}</div>
                      <div className="mt-0.5 font-display text-xl font-bold text-nosh-ink">{c.value}</div>
                    </div>
                  </div>
                );
                return c.href ? (
                  <a key={c.label} href={c.href} data-testid="contact-phone" className="block">{inner}</a>
                ) : (
                  <div key={c.label}>{inner}</div>
                );
              })}

              <div className="flex items-center gap-4 rounded-2xl border border-nosh-border bg-white p-5">
                <div className="font-poster text-[11px] font-bold uppercase tracking-widest text-nosh-gold">Follow</div>
                <div className="flex gap-3">
                  <a href="#" aria-label="Instagram" className="grid h-11 w-11 place-items-center rounded-full bg-nosh-green text-nosh-cream transition-colors hover:bg-nosh-ink">
                    <Instagram className="h-5 w-5" />
                  </a>
                  <a href="#" aria-label="Facebook" className="grid h-11 w-11 place-items-center rounded-full bg-nosh-green text-nosh-cream transition-colors hover:bg-nosh-ink">
                    <Facebook className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="rounded-[2rem] border border-nosh-border bg-nosh-surface p-6 md:p-9">
              <InquiryForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
